import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoFeed from './components/VideoFeed';
import MusicPlayer from './components/MusicPlayer';
import UploadModal from './components/UploadModal';
import AdsBanner from '@/components/feature/AdsBanner';
import FeaturedCarousel from './components/FeaturedCarousel';
import TikTokFeed from './components/TikTokFeed';
import YoutubeFeed from './components/YoutubeFeed';
import ConcertSection from './components/ConcertSection';
import LiveSection from './components/LiveSection';
import { useAuth } from '@/context/AuthContext';
import {
  fetchMediaPosts,
  fetchMediaTracks,
  getCertifiedArtists,
  MEDIA_CATEGORIES,
  type MediaPost,
  type MediaTrack,
} from '@/lib/media';
import { demoVideos, demoTracks } from '@/mocks/media';

type Tab = 'videos' | 'musique' | 'tiktok' | 'youtube' | 'concerts' | 'live';

function Media() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(
    urlTab && ['videos', 'musique', 'tiktok', 'youtube', 'concerts', 'live'].includes(urlTab)
      ? urlTab
      : 'videos',
  );
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('Tous');
  const [posts, setPosts] = useState<MediaPost[]>([]);
  const [tracks, setTracks] = useState<MediaTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, t] = await Promise.all([fetchMediaPosts(), fetchMediaTracks()]);
      setPosts(p);
      setTracks(t);
    } catch (err) {
      setError('Impossible de charger le contenu. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Synchronise l'onglet actif dans l'URL (pour les liens partagés type /media?tab=live)
  useEffect(() => {
    const params = new URLSearchParams();
    if (tab !== 'videos') params.set('tab', tab);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const allVideos = useMemo<MediaPost[]>(() => [...posts, ...demoVideos], [posts]);
  const allTracks = useMemo<MediaTrack[]>(() => [...tracks, ...demoTracks], [tracks]);

  const certifiedArtists = useMemo(() => getCertifiedArtists(allVideos), [allVideos]);

  const filteredVideos = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allVideos.filter((v) => {
      const matchCat = category === 'Tous' || v.category === category;
      const matchSearch =
        !q ||
        v.title.toLowerCase().includes(q) ||
        (v.artist_name || '').toLowerCase().includes(q) ||
        (v.location || '').toLowerCase().includes(q) ||
        (v.description || '').toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [allVideos, search, category]);

  const filteredTracks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allTracks.filter((t) => {
      const matchCat = category === 'Tous' || t.category === category;
      const matchSearch =
        !q ||
        t.title.toLowerCase().includes(q) ||
        (t.artist_name || '').toLowerCase().includes(q) ||
        (t.location || '').toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [allTracks, search, category]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16 md:pt-20">
        {/* Header */}
        <section className="bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <span className="inline-block text-sm font-semibold text-accent-400 uppercase tracking-wider mb-3">
                  Média
                </span>
                <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50 mb-3">
                  Regardez, écoutez, découvrez les talents d&apos;Afrique
                </h1>
                <p className="text-base md:text-lg text-foreground-400 max-w-2xl">
                  Artistes, comédiens, créateurs de contenu et youtubeurs partagent leurs vidéos,
                  tutoriels, acapellas et morceaux. Du Togo au monde entier.
                </p>
              </div>
              {user ? (
                <button
                  onClick={() => setUploadOpen(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap shrink-0"
                >
                  <i className="ri-add-line text-lg" />
                  Publier mon contenu
                </button>
              ) : (
                <a
                  href="/auth/login"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap shrink-0"
                >
                  <i className="ri-login-box-line text-lg" />
                  Se connecter pour publier
                </a>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1 mt-8 bg-background-50 rounded-full w-full overflow-x-auto no-scrollbar">
              <button
                onClick={() => setTab('videos')}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  tab === 'videos'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-video-line mr-1.5" />
                Vidéos
              </button>
              <button
                onClick={() => setTab('musique')}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  tab === 'musique'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-music-2-line mr-1.5" />
                Musique
              </button>
              <button
                onClick={() => setTab('tiktok')}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  tab === 'tiktok'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-tiktok-line mr-1.5" />
                TikTok
              </button>
              <button
                onClick={() => setTab('youtube')}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  tab === 'youtube'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-youtube-line mr-1.5" />
                YouTube
              </button>
              <button
                onClick={() => setTab('concerts')}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  tab === 'concerts'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-ticket-2-line mr-1.5" />
                Concerts
              </button>
              <button
                onClick={() => setTab('live')}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  tab === 'live'
                    ? 'bg-primary-500 text-foreground-950'
                    : 'text-foreground-400 hover:text-foreground-50'
                }`}
              >
                <i className="ri-live-line mr-1.5" />
                Live
              </button>
            </div>

            {(tab === 'videos' || tab === 'musique') && (
            <>
            {/* Recherche */}
            <div className="mt-5 relative max-w-xl">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-base" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un artiste, une vidéo, un morceau…"
                className="w-full pl-10 pr-4 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>

            {/* Catégories */}
            <div className="mt-4 flex items-center gap-2 overflow-x-auto no-scrollbar">
              {['Tous', ...MEDIA_CATEGORIES].map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    category === c
                      ? 'bg-accent-500 text-foreground-950'
                      : 'bg-background-50 text-foreground-400 hover:text-foreground-50 border border-background-300/60'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            </>
            )}
          </div>
        </section>

        {/* Contenu */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <AdsBanner pageKey="media" />
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex items-center gap-3 text-foreground-400">
                <i className="ri-loader-4-line animate-spin text-2xl" />
                <span>Chargement du contenu…</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-error-warning-line text-3xl text-accent-400" />
              </div>
              <p className="text-foreground-400 mb-4">{error}</p>
              <button
                onClick={loadData}
                className="px-5 py-2.5 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : tab === 'videos' ? (
            <>
              <FeaturedCarousel videos={allVideos} certifiedArtists={certifiedArtists} />
              <VideoFeed videos={filteredVideos} certifiedArtists={certifiedArtists} />
            </>
          ) : tab === 'musique' ? (
            <MusicPlayer tracks={filteredTracks} />
          ) : tab === 'tiktok' ? (
            <TikTokFeed />
          ) : tab === 'youtube' ? (
            <YoutubeFeed />
          ) : tab === 'live' ? (
            <LiveSection />
          ) : (
            <ConcertSection />
          )}
        </section>
      </main>

      <Footer />

      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onPublished={loadData}
      />
    </div>
  );
}

export default Media;